import {
  FilterDefinition,
  Operators,
  Rule,
  RuleGroup,
} from "@monolith-forensics/monolith-ui";

/*
    POST /acquisitions/query does not understand monolith-ui RuleGroup
    filters — it only accepts the legacy Condition[] shape consumed by the
    server's queryConditionResolver (monolith-api
    src/services/utilities/Query-Condition-Resolver.js). This converts the
    Table filter state into that shape.

    Invariants the resolver relies on:
    - conditions are always ANDed (RuleGroup "or" combinator cannot be
      represented)
    - `operator.type` is derived from the filter definition's inputType
      (datetime/date -> "date", number -> "number", else "text"), NOT from a
      customFields lookup — this is what tells the resolver to treat standard
      AND custom date fields as dates
    - standard fields need a scalar value for equals/contains/doesNotContain;
      custom fields and every other operator keep the array
    - "is empty" is any operator with an empty value; "is not empty" is only
      expressible for custom fields (negative operator + empty value)
*/

export interface AcquisitionCondition {
  field: string;
  operator: { operator: string; type: string };
  value: string | number | (string | number)[];
}

const OPERATOR_MAP: Record<string, string> = {
  [Operators.Equals.value]: "equals",
  [Operators.NotEquals.value]: "doesNotEqual",
  [Operators.GreaterThan.value]: "isAfter",
  [Operators.LessThan.value]: "isBefore",
  [Operators.Contains.value]: "contains",
  [Operators.DoesNotContain.value]: "doesNotContain",
  [Operators.In.value]: "isAnyOf",
  [Operators.NIn.value]: "isNoneOf",
  [Operators.Between.value]: "isBetween",
  [Operators.IsEmpty.value]: "equals",
  [Operators.IsNotEmpty.value]: "doesNotEqual",
};

const VALUELESS_OPERATORS: string[] = [
  Operators.IsEmpty.value,
  Operators.IsNotEmpty.value,
];

const CUSTOM_FIELD_PREFIX = "custom_field_";

const isCustomField = (field: string) => field.startsWith(CUSTOM_FIELD_PREFIX);

export const isCompleteRule = (rule: Rule) => {
  if (VALUELESS_OPERATORS.includes(rule.operator.value)) {
    return true;
  }
  return (
    !!rule.value &&
    rule.value.length > 0 &&
    !rule.value.some((v) => v === undefined || v === null || v === "")
  );
};

/** Resolver-relevant `operator.type` per filter dimension (only "date" affects SQL). */
const buildTypeMap = (
  filterDefinitions: FilterDefinition[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const def of filterDefinitions) {
    map[def.dataField] =
      def.inputType === "datetime" || def.inputType === "date"
        ? "date"
        : def.inputType === "number"
        ? "number"
        : "text";
  }
  return map;
};

// The resolver binds these straight into `BETWEEN ? AND ?` on DATETIME
// columns, so a day-resolution upper bound would silently exclude the end
// day for any row after midnight. isBetween is only ever a date operator
// here, so keying on the operator is enough.
const extendDateRange = (value: (string | number)[]) =>
  value.length === 2 ? [value[0], `${value[1]} 23:59:59`] : value;

/**
 * Packs a rule's value to match how `queryConditionResolver` consumes args for
 * each operator. Valueless operators (is/is-not empty) send an empty array;
 * custom fields always keep the array; standard fields need a scalar for
 * equals/contains/doesNotContain and the array for everything else.
 */
const resolveConditionValue = (
  rule: Rule,
  operator: string
): AcquisitionCondition["value"] => {
  if (VALUELESS_OPERATORS.includes(rule.operator.value)) {
    return [];
  }

  const value = rule.value ?? [];

  if (isCustomField(rule.dataField)) {
    return operator === "isBetween" ? extendDateRange(value) : value;
  }

  switch (operator) {
    case "equals":
    case "contains":
    case "doesNotContain":
      return value[0] ?? "";
    case "isBetween":
      return extendDateRange(value);
    default:
      return value;
  }
};

export const ruleGroupToConditions = (
  filter: RuleGroup | undefined,
  filterDefinitions?: FilterDefinition[]
): AcquisitionCondition[] => {
  if (!filter?.rules?.length) {
    return [];
  }

  const typeMap = buildTypeMap(filterDefinitions ?? []);

  return filter.rules
    .filter(isCompleteRule)
    .filter((rule) => !!OPERATOR_MAP[rule.operator.value])
    .map((rule) => {
      const operator = OPERATOR_MAP[rule.operator.value];

      return {
        field: rule.dataField,
        operator: {
          operator,
          // Valueless operators must take the resolver's text path: its
          // custom-field date branch has no empty-value handling and would
          // emit a `?` placeholder with no bind arg. The text branches
          // rewrite empty values to `(field IS NULL OR field = '')` before
          // ever reading the type, so this is a noop for standard fields.
          type: VALUELESS_OPERATORS.includes(rule.operator.value)
            ? "text"
            : typeMap[rule.dataField] || "text",
        },
        value: resolveConditionValue(rule, operator),
      };
    });
};
